import React from 'react';

import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { IHistoricalOrder } from '@rbi-ctg/menu';
import ActionButton from 'components/action-button';
import { FavoriteOrderButton } from 'components/favorite-order-button';
import { RecentOrderCard } from 'components/recent-order-card-list-item';
import useOrderNumber from 'hooks/use-order-number';
import { useFlag } from 'state/launchdarkly';
import { useReorder } from 'state/order/hooks/use-reorder';
import { useServiceModeContext } from 'state/service-mode';
import { useCreateOrderAriaLabel } from 'utils/accessibility/aria';
import { LaunchDarklyFlag } from 'utils/launchdarkly';

import { theme } from './theme';

const Container = styled.div``;

const Header = styled.div`
  padding: 4px 16px;
  background-color: ${theme.recentOrderHeaderBackgroundColor};
  display: flex;
  align-items: center;
  justify-content: space-between;
  button {
    font-size: 1rem;
    padding: 0 16px;
    height: 32px;
  }
`;

const Info = styled.div`
  display: flex;
  align-items: center;
`;

const FormattedDate = styled.p`
  font-size: 1rem;
  font-weight: 800;
`;

const OrderNumber = styled.p`
  font-size: 1rem;
  margin-left: 8px;
`;

interface RecentOrderCardGroupProps {
  order: IHistoricalOrder;
}

const RecentOrderCardGroup: React.FC<RecentOrderCardGroupProps> = ({ order }) => {
  const { formatMessage } = useIntl();
  const enableFavorites = useFlag(LaunchDarklyFlag.ENABLE_FAVORITES);
  const createAriaLabel = useCreateOrderAriaLabel();
  const { handleReorderClick, disabled } = useReorder(order);
  const { isCatering } = useServiceModeContext();
  const reorderCartEntries = order.cart.reorderCartEntries;
  const orderNumber = useOrderNumber(order);
  const formattedDate = new Date(order.createdAt).toLocaleString(
    formatMessage({ id: 'localeDateTime' }),
    {
      month: 'long',
      day: 'numeric',
    }
  );

  const handleReorder = () => {
    handleReorderClick();
  };

  if (!reorderCartEntries?.length) {
    return null;
  }

  return (
    <Container data-testid="recent-order-card-group">
      <Header>
        <Info>
          <FormattedDate> {formattedDate}</FormattedDate>
          {enableFavorites && !isCatering && <FavoriteOrderButton order={order} />}
          {orderNumber && (
            <OrderNumber>
              {formatMessage({ id: 'order' })} #{orderNumber}
            </OrderNumber>
          )}
        </Info>
        <ActionButton
          data-testid="reorder-button"
          onClick={handleReorder}
          disabled={disabled}
          aria-label={createAriaLabel({
            buttonType: 'reorder',
            order,
          })}
        >
          {formatMessage({ id: 'reorder' })}
        </ActionButton>
      </Header>
      {reorderCartEntries.map(cartEntry => {
        if (cartEntry.isDonation) {
          return null;
        }

        return <RecentOrderCard key={cartEntry.lineId} cartEntry={cartEntry} />;
      })}
    </Container>
  );
};

export default RecentOrderCardGroup;
